import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ExclamationCircle } from "react-bootstrap-icons";

// CSS
import "./style.css";

import eye_show from "../../assets/img/icon/eye_show.png";
import eye_hide from "../../assets/img/icon/eye_hide.png";

const RATE_LIMIT = {
  WARNING: 5,
  COOLDOWN_5: [6, 10],
  COOLDOWN_10: [11, 49],
  BLOCK_5_MIN: [50, 99],
  BLOCK_10_MIN: [100, 149],
  BLOCK_60_MIN: [150, Infinity],
};

function Login(props) {
  const [keepLogin, setKeepLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [validationEmail, setValidationEmail] = useState();
  const [password, setPassword] = useState("");
  const [validationPassword, setValidationPassword] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState();

  const handlerSetKeepLogin = () => setKeepLogin(!keepLogin);

  const [passwordShown, setPasswordShown] = useState(false);
  const [failedLoginCount, setFailedLoginCount] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockUntil, setLockUntil] = useState(null);
  const [retryAfter, setRetryAfter] = useState(0);
  const [warningVisible, setWarningVisible] = useState(false);
  const [cooldownVisible, setCooldownVisible] = useState(false);
  const [blockVisible, setBlockVisible] = useState(false);

  const resetFailedLoginCounter = () => {
    setFailedLoginCount(0);
    localStorage.setItem("failedLoginCount", "0");
  };

  const setLockFromAttempt = (attempt) => {
    let seconds = 0;

    if (attempt >= 150) seconds = 60 * 60;
    else if (attempt >= 100) seconds = 15 * 60;
    else if (attempt >= 50) seconds = 5 * 60;
    else if (attempt >= 11) seconds = 10;
    else if (attempt >= 6) seconds = 5;

    if (seconds > 0) {
      const end = Date.now() + seconds * 1000;
      setLockUntil(end);
      setIsLocked(true);
      setRetryAfter(seconds);
      localStorage.setItem("loginLockUntil", String(end));
      if (attempt >= 50) {
        setBlockVisible(true);
        setCooldownVisible(false);
      } else {
        setCooldownVisible(true);
        setBlockVisible(false);
      }
    }
  };

  useEffect(() => {
    const savedCount = Number(localStorage.getItem("failedLoginCount") || 0);
    const savedLockUntil = Number(localStorage.getItem("loginLockUntil") || 0);

    setFailedLoginCount(savedCount);

    if (savedLockUntil > Date.now()) {
      setLockUntil(savedLockUntil);
      setIsLocked(true);
      setRetryAfter(Math.ceil((savedLockUntil - Date.now()) / 1000));
      const attempt = savedCount || 1;

      if (attempt >= 50) {
        setBlockVisible(true);
      } else {
        setCooldownVisible(true);
      }
    }
  }, []);

  useEffect(() => {
    if (!isLocked || !lockUntil) return;

    const timer = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((lockUntil - Date.now()) / 1000));

      setRetryAfter(remaining);

      if (remaining <= 0) {
        clearInterval(timer);
        setIsLocked(false);
        setLockUntil(null);
        setCooldownVisible(false);
        setBlockVisible(false);
        setRetryAfter(0);
        localStorage.removeItem("loginLockUntil");

        // reset failed count after lock expires
        resetFailedLoginCounter();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [isLocked, lockUntil]);

  const handleFailedLogin = (attempt, errorCode) => {
    const count = Number(attempt || 0);

    setFailedLoginCount(count);
    localStorage.setItem("failedLoginCount", String(count));

    if (count >= 1 && count <= 4) {
      setError({ code: errorCode });
      setWarningVisible(false);
      setCooldownVisible(false);
      setBlockVisible(false);
      setLoading(false);
      return;
    }

    if (count === 5) {
      setError({ code: errorCode });
      setWarningVisible(true);
      setCooldownVisible(false);
      setBlockVisible(false);
      setLoading(false);
      return;
    }

    if (count >= 6 && count <= 10) {
      setLockFromAttempt(count);
      setError({ code: errorCode });
      setWarningVisible(false);
      setLoading(false);
      return;
    }

    if (count >= 11 && count <= 49) {
      setLockFromAttempt(count);
      setError({ code: errorCode });
      setWarningVisible(false);
      setLoading(false);
      return;
    }

    if (count >= 50 && count <= 99) {
      setLockFromAttempt(count);
      setError({ code: errorCode });
      setWarningVisible(false);
      setLoading(false);
      return;
    }

    if (count >= 100 && count <= 149) {
      setLockFromAttempt(count);
      setError({ code: errorCode });
      setWarningVisible(false);
      setLoading(false);
      return;
    }

    if (count >= 150) {
      setLockFromAttempt(count);
      setError({ code: errorCode });
      setWarningVisible(false);
      setLoading(false);
      return;
    }
  };

  const togglePasswordVisiblity = () => {
    setPasswordShown((prev) => !prev);
  };

  const doValidation = () => {
    if (isLocked) return;

    setValidationEmail(false);
    setValidationPassword(false);
    setError(null);

    if (email === "" && password === "") {
      setValidationEmail(true);
      setValidationPassword(true);
      setEmail("");
      setPassword("");
      return;
    }

    if (email === "") {
      setValidationEmail(true);
      setEmail("");
      return;
    }

    if (password === "") {
      setValidationPassword(true);
      setPassword("");
      return;
    }

    doLogin();
  };

  const doLogin = () => {
    setLoading(true);
    setValidationEmail(false);
    setValidationPassword(false);
    setError(null);

    const payload = {
      email,
      password,
      keepLogin,
    };

    fetch("/_api/main/login", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((result) => {
        console.log("result:", result);

        if (result.success) {
          resetFailedLoginCounter();
          window.location.href = result.redirect_uri;
          return;
        }

        // format response normal error
        if (result.error && result.error.code) {
          const attempt = Number(result.attempt || failedLoginCount + 1);
          const errorCode = result.error.code;
          handleFailedLogin(attempt, errorCode);
          return;
        }

        // format response rate limited
        if (result.rate_limited) {
          const attempt = Number(result.attempt || failedLoginCount + 1);
          const errorCode = result.error?.code || 40002;
          handleFailedLogin(attempt, errorCode);

          if (result.retry_after) {
            const lockSeconds = Number(result.retry_after);
            const end = Date.now() + lockSeconds * 1000;
            setLockUntil(end);
            setIsLocked(true);
            setRetryAfter(lockSeconds);
            localStorage.setItem("loginLockUntil", String(end));
          }

          return;
        }

        setError(true);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  };
  return (
    <div
      className="main-container"
      data-page={props.appName}
      data-aos={"fade-up"}
    >
      {warningVisible && (
        <div className="modal-overlay">
          <div className="modal-popup warning-popup">
            <button
              className="close-btn"
              onClick={() => setWarningVisible(false)}
            >
              ×
            </button>
            <div className="popup-icon warning-icon">
              <ExclamationCircle size={42} />
            </div>
            <h4 className="popup-title">Terlalu banyak percobaan login</h4>
            <p className="popup-message">
              Username atau password salah beberapa kali. Jika percobaan
              berulang, akan masuk ke mode cooldown.
            </p>
            <button
              className="popup-btn"
              onClick={() => setWarningVisible(false)}
            >
              Tutup
            </button>
          </div>
        </div>
      )}
      {cooldownVisible && (
        <div className="modal-overlay">
          <div className="modal-popup cooldown-popup">
            <button
              className="close-btn"
              onClick={() => setCooldownVisible(false)}
            >
              ×
            </button>
            <div className="popup-icon cooldown-icon">
              <span className="clock-icon">◔</span>
            </div>
            <h4 className="popup-title">Terlalu banyak percobaan login</h4>
            <p className="popup-message">Silakan tunggu beberapa detik.</p>

            <div className="countdown-timer">
              <span>Cooldown</span>
              <div className="timer-display">
                {String(Math.floor(retryAfter / 60)).padStart(2, "0")}:
                {String(retryAfter % 60).padStart(2, "0")}
              </div>
            </div>

            <button className="popup-btn" disabled>
              Tutup
            </button>
          </div>
        </div>
      )}
      {blockVisible && (
        <div className="modal-overlay">
          <div className="modal-popup block-popup">
            <button
              className="close-btn"
              onClick={() => setBlockVisible(false)}
            >
              ×
            </button>
            <div className="popup-icon block-icon">
              <span className="lock-icon">🔒</span>
            </div>
            <h4 className="popup-title">Akun Anda diblokir</h4>
            <p className="popup-message">
              Terlalu banyak percobaan login. Akun Anda telah diblokir sementara
              untuk keamanan.
            </p>

            <div className="block-timer">
              <div className="timer-label">Waktu blokir</div>
              <div className="timer-display">
                {String(Math.floor(retryAfter / 60)).padStart(2, "0")}:
                {String(retryAfter % 60).padStart(2, "0")}
              </div>
            </div>

            <button className="popup-btn" disabled>
              Tutup
            </button>
          </div>
        </div>
      )}
      <div className="form-title mb-3">
        <div className="mb-0">
          <h5>Masuk</h5>
        </div>
        <div>
          <p>Silahkan masuk menggunakan akun kamu</p>
        </div>
      </div>

      <div className="main-form">
        <form>
          <div className="mb-2">
            <label htmlFor="email">Email</label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="text"
              name="email"
              id="email"
              placeholder="Masukkan email / username"
              className={
                validationEmail || (error && error.code === 40001)
                  ? "form-control px-3 is-invalid"
                  : "form-control px-3"
              }
              required
              disabled={isLocked}
            />
            {validationEmail && (
              <div className="invalid-feedback d-block">
                <ExclamationCircle /> Email / username tidak boleh kosong
              </div>
            )}
            {error && error.code === 40001 && (
              <div className="invalid-feedback d-block">
                <ExclamationCircle size={14} /> Email tidak ditemukan
              </div>
            )}
          </div>
          <div className="mb-3">
            <label htmlFor="password">Password</label>
            <div className="d-flex input-password">
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type={passwordShown ? "text" : "password"}
                name="password"
                id="password"
                placeholder="Masukkan password"
                className={
                  validationPassword || (error && error.code === 40002)
                    ? "form-control px-3 is-invalid"
                    : "form-control px-3"
                }
                required
                disabled={isLocked}
              />
              <div className="input-group-append form-control-feedback">
                <div className="input-group-text toggle-password">
                  <img
                    onClick={togglePasswordVisiblity}
                    className="icon-btn"
                    alt="eye"
                    src={passwordShown ? eye_hide : eye_show}
                    style={{ width: "25px", height: "25px" }}
                  />
                </div>
              </div>
            </div>
             {validationPassword && (
              <div className="invalid-feedback d-block">
                <ExclamationCircle /> Password tidak boleh kosong
              </div>
            )}
            {error && error.code === 40002 && (
              <div className="invalid-feedback d-block">
                <ExclamationCircle size={14} /> Password tidak sesuai
              </div>
            )}
          </div>

          <div className="mb-4 d-flex">
            <div className="col-6 form-check">
               <input
                onChange={() => setKeepLogin((prev) => !prev)}
                type="checkbox"
                name="keepLogin"
                id="keepLogin"
                className="form-check-input"
                disabled={isLocked}
              />
              <label htmlFor="keepLogin" className="form-check-label">
                Ingat saya
              </label>
            </div>
            {error && (
              <div className="col-6 forgot-password">
                <Link className="link" to="/forgot">Lupa password?</Link>
              </div>
            )}
          </div>

          <button
            type="submit"
            className="form-control mb-2"
            onClick={(e) => {
              e.preventDefault();
              doValidation();
            }}
            disabled={isLocked || loading}
          >
            {loading ? (
              <div className="loading-container">
                <span className="spinner-border text-light loading" role="status"></span>
                Tunggu sebentar
              </div>
            ) : (
              "Masuk"
            )}
          </button>

          <div className="col-12 text-center">
            <span data-testid="not-have-account">Belum punya akun?</span> <br />
            <span data-testid="not-have-account">Silahkan hubungi sekolah</span>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Login;
